/**
 * MonthlyBudgetCard — quick snapshot of this month's spending vs budget
 * for each category. Lives at the top of the Budget tab so you can see
 * where you stand at a glance without scrolling to Variable Spending.
 */
import React, { useState } from 'react';
import { fc } from '../../utils';

export function MonthlyBudgetCard({ cats, catBudgets, vd, varBudget, varTot }) {
  const [expanded, setExpanded] = useState(true);

  // Only show categories that have a budget set or have spending logged this month
  const rows = cats
    .map((cat) => {
      const budget = (catBudgets || {})[cat.id] || 0;
      const spent = (vd[cat.id] || []).reduce((s, x) => s + x.amt, 0);
      return { cat, budget, spent };
    })
    .filter(({ budget, spent }) => budget > 0 || spent > 0);

  const totalBudget = varBudget;
  const totalSpent = varTot;
  const totalOver = totalSpent > totalBudget && totalBudget > 0;
  const totalPct = totalBudget > 0 ? Math.min(100, Math.round((totalSpent / totalBudget) * 100)) : 0;

  // Spending pace — compare % of month elapsed vs % of budget used
  const now = new Date();
  const dayOfMonth = now.getDate();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const monthPct = Math.round((dayOfMonth / daysInMonth) * 100);
  const paceStatus = totalBudget > 0
    ? totalPct > monthPct + 15 ? 'over' : totalPct < monthPct - 15 ? 'under' : 'on'
    : null;
  const paceColor = paceStatus === 'over' ? 'var(--red)' : paceStatus === 'under' ? 'var(--grn)' : 'var(--mut)';

  if (rows.length === 0) return null;

  return (
    <div className="card cp" style={{ marginBottom: 12 }}>
      {/* Header */}
      <button
        onClick={() => setExpanded((e) => !e)}
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: expanded ? 10 : 0 }}
      >
        <div style={{ textAlign: 'left' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--mut)', textTransform: 'uppercase', letterSpacing: '.5px' }}>
            📊 Monthly Spending
          </div>
          {!expanded && (
            <div style={{ fontSize: 12, fontWeight: 700, color: totalOver ? 'var(--red)' : 'var(--txt)', marginTop: 2 }}>
              {fc(totalSpent)}{totalBudget > 0 ? ` / ${fc(totalBudget)}` : ''}
              {totalOver && <span style={{ color: 'var(--red)', fontWeight: 400, fontSize: 11, marginLeft: 5 }}>over budget</span>}
            </div>
          )}
        </div>
        <span style={{ fontSize: 10, color: 'var(--mut)' }}>{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <>
          {/* Overall bar */}
          {totalBudget > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                <span style={{ fontSize: 11, color: 'var(--mut)' }}>Total this month</span>
                <span style={{ fontSize: 13, fontWeight: 800, color: totalOver ? 'var(--red)' : 'var(--txt)' }}>
                  {fc(totalSpent)}
                  <span style={{ fontSize: 10, fontWeight: 400, color: 'var(--mut)', marginLeft: 4 }}>/ {fc(totalBudget)}</span>
                </span>
              </div>
              <div className="pw">
                <div
                  className="pb"
                  style={{
                    background: totalOver ? 'var(--red)' : totalPct > 80 ? 'var(--ylw)' : 'var(--grn)',
                    width: Math.min(100, totalPct) + '%',
                  }}
                />
              </div>
              <div style={{ fontSize: 10, color: totalOver ? 'var(--red)' : 'var(--mut)', marginTop: 2, textAlign: 'right', fontWeight: totalOver ? 700 : 400 }}>
                {totalOver
                  ? `${fc(totalSpent - totalBudget)} over`
                  : `${fc(totalBudget - totalSpent)} left`}
              </div>
              {/* Spending pace */}
              {paceStatus && (
                <div style={{ marginTop: 8, padding: '6px 10px', borderRadius: 7, background: 'var(--surf2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 10, color: 'var(--mut)' }}>Day {dayOfMonth} of {daysInMonth} · {monthPct}% through month</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: paceColor }}>
                    {paceStatus === 'over' ? '🔴 Ahead of pace' : paceStatus === 'under' ? '🟢 Under pace' : '🟡 On pace'}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Per-category rows */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {rows.map(({ cat, budget, spent }) => {
              const pct = budget > 0 ? Math.min(100, Math.round((spent / budget) * 100)) : 0;
              const over = spent > budget && budget > 0;
              const barColor = over ? 'var(--red)' : pct > 80 ? 'var(--ylw)' : cat.col || 'var(--ylw)';
              return (
                <div key={cat.id}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                    <span style={{ fontSize: 14 }}>{cat.em || '💳'}</span>
                    <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: 'var(--txt)' }}>{cat.name}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: over ? 'var(--red)' : 'var(--txt)' }}>
                      {fc(spent)}
                    </span>
                    {budget > 0 && (
                      <span style={{ fontSize: 10, color: 'var(--mut)' }}>/ {fc(budget)}</span>
                    )}
                  </div>
                  {budget > 0 && (
                    <div className="pw" style={{ height: 4 }}>
                      <div className="pb" style={{ background: barColor, width: pct + '%', height: 4 }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {totalBudget <= 0 && (
            <div style={{ fontSize: 11, color: 'var(--mut)', marginTop: 4 }}>
              Set category limits in 🗺️ Plan → Income & Budget to track spending against a budget.
            </div>
          )}
        </>
      )}
    </div>
  );
}
